import React, { useState, useEffect } from 'react';
import {
    Container, Typography, Grid, Box, Paper, TextField, Button,
    Snackbar, Alert, CircularProgress, MenuItem, Tooltip, Tabs, Tab,
    Card, CardContent, Divider, ButtonGroup
} from '@mui/material';
import { 
    Send, Download, PictureAsPdf, Description, Assignment, 
    Info, ArrowForward, HelpOutline 
} from '@mui/icons-material';
import SidebarNavigation from '../utils/SideBarNavigation';
import firebaseApp, { KEY_REF } from '../adm/admin/firebaseConfig';
import { getDatabase, ref, push, get } from 'firebase/database';
import TitleSection from '../utils/TittleTheme';
import { getConfig } from '../adm/ConfigContext';

const ServiceLetterPage = () => {
    const [currentTab, setCurrentTab] = useState(0); // 0 = Guia de Serviços (PNTP), 1 = Solicitar Serviço
    const [formData, setFormData] = useState({ email: '', letterType: '', description: '' });
    const [ticketNumber, setTicketNumber] = useState(null);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [loadingParams, setLoadingParams] = useState(true);
    const [submissionLimitReached, setSubmissionLimitReached] = useState(false);
    
    // Estado para guardar os parâmetros da Carta de Serviço (Texto e/ou PDF inseridos pelo Hebert)
    const [cartaParams, setCartaParams] = useState({ texto: '', pdfUrl: '' });

    const config = getConfig();
    const databaseInstance = getDatabase(firebaseApp);
    const lettersRef = ref(databaseInstance, `config_parametros/cartadeservico/`);

    const letterTypes = [
        "Solicitação de Certidão", "Requerimento de Serviços Urbanos", "Solicitação de Informações Tributárias",
        "Solicitação de Serviços Educacionais", "Solicitação de Serviços de Saúde", "Solicitação de Licença Ambiental",
        "Requisição de Transporte Escolar", "Pedidos de Iluminação Pública", "Fiscalização de Obras", 
        "Requisição de Alvarás", "Registro de Reclamações"
    ];

    const generateTicketNumber = () => Math.floor(1000000000 + Math.random() * 9000000000);

    useEffect(() => {
        checkSubmissionLimit();
        fetchCartaParameters();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Carrega o texto ou o link do PDF configurado pelo administrador (Hebert)
    const fetchCartaParameters = async () => {
        setLoadingParams(true);
        try {
            const paramsRef = ref(databaseInstance, 'config_parametros/cartadeservico');
            const snapshot = await get(paramsRef);
            if (snapshot.exists()) {
                setCartaParams(snapshot.val());
            }
        } catch (error) {
            console.error("Erro ao carregar parâmetros da Carta de Serviço:", error);
        } finally {
            setLoadingParams(false);
        }
    };

    const checkSubmissionLimit = () => {
        const submissionData = JSON.parse(localStorage.getItem('submissionDataCarta')) || { count: 0, lastSubmission: null };
        const now = new Date();
        const lastSubmissionDate = new Date(submissionData.lastSubmission);

        if (submissionData.count >= 3 && now - lastSubmissionDate < 24 * 60 * 60 * 1000) {
            setSubmissionLimitReached(true);
        } else if (now - lastSubmissionDate >= 24 * 60 * 60 * 1000) {
            localStorage.setItem('submissionDataCarta', JSON.stringify({ count: 0, lastSubmission: now }));
            setSubmissionLimitReached(false);
        }
    };

    const incrementSubmissionCount = () => {
        const submissionData = JSON.parse(localStorage.getItem('submissionDataCarta')) || { count: 0, lastSubmission: null };
        submissionData.count += 1;
        submissionData.lastSubmission = new Date();
        localStorage.setItem('submissionDataCarta', JSON.stringify(submissionData));
    };

    const validateForm = () => {
        return (
            formData.email.trim() &&
            formData.letterType &&
            formData.description.trim().length >= 10
        );
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            alert("Preencha todos os campos corretamente.");
            return;
        }

        if (submissionLimitReached) {
            alert("Você atingiu o limite diário de 3 cartas de serviço.");
            return;
        }

        const newTicket = generateTicketNumber();
        setLoading(true);
        await push(lettersRef, {
            ...formData,
            date: new Date().toLocaleDateString('pt-BR'),
            ticket: newTicket,
            status: "Criado"
        });
        setTicketNumber(newTicket);
        incrementSubmissionCount();
        setFormData({ email: '', letterType: '', description: '' });
        setSuccess(true);
        setLoading(false);
        checkSubmissionLimit();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    return (
        <Container maxWidth="lg" sx={{ py: 5, display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ flex: 4 }}>
                <TitleSection 
                    title="Carta de Serviços ao Usuário" 
                    subtitle="Informações sobre os serviços prestados, formas de acesso e compromissos com o cidadão." 
                />

                {/* Abas de Navegação interna */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs value={currentTab} onChange={(e, val) => setCurrentTab(val)} indicatorColor="primary" textColor="primary">
                        <Tab label="Guia e Documentos Oficiais" icon={<Description />} iconPosition="start" />
                        <Tab label="Solicitar Serviço Online" icon={<Assignment />} iconPosition="start" />
                    </Tabs>
                </Box>

                {currentTab === 0 ? (
                    /* ABA 0: VISUALIZAÇÃO E DOWNLOAD DA CARTA (PNTP 14.3) */
                    <Box display="flex" flexDirection="column" gap={3}>
                        
                        {/* Box de Downloads em Destaque */}
                        <Paper elevation={2} sx={{ p: 3, borderRadius: 2, borderLeft: `6px solid ${config?.color || '#1976d2'}` }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Info color="primary" sx={{ color: config?.color }} /> Consulta e Download da Carta de Serviços
                            </Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Conforme exigido pela Lei Federal nº 13.460/2017 e o item 14.3 do PNTP, baixe o documento consolidado ou consulte as diretrizes abaixo.
                            </Typography>
                            
                            <Grid container spacing={2}>
                                {cartaParams.pdfUrl ? (
                                    <Grid item xs={12} sm={6}>
                                        <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: 2 }}>
                                            <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', py: '8px !important' }}>
                                                <PictureAsPdf sx={{ color: '#d32f2f', fontSize: 40 }} />
                                                <Box flexGrow={1}>
                                                    <Typography variant="subtitle2" fontWeight="bold">Versão Oficial em PDF</Typography>
                                                    <Typography variant="caption" color="textSecondary">Documento Integral</Typography>
                                                </Box>
                                                <Button 
                                                    variant="contained" 
                                                    size="small"
                                                    startIcon={<Download />}
                                                    href={cartaParams.pdfUrl}
                                                    target="_blank"
                                                    sx={{ backgroundColor: '#d32f2f', '&:hover': { backgroundColor: '#b71c1c' } }}
                                                >
                                                    Baixar
                                                </Button>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ) : (
                                    <Grid item xs={12}>
                                        <Alert severity="info">
                                            A administração disponibilizou a Carta de Serviços em formato de texto na íntegra abaixo.
                                        </Alert>
                                    </Grid>
                                )}

                                <Grid item xs={12} sm={6}>
                                    <Card variant="outlined" sx={{ display: 'flex', alignItems: 'center', p: 1, borderRadius: 2, height: '100%' }}>
                                        <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', py: '8px !important' }}>
                                            <Assignment sx={{ color: config?.color || '#1976d2', fontSize: 38 }} />
                                            <Box flexGrow={1}>
                                                <Typography variant="subtitle2" fontWeight="bold">Precisa de um serviço?</Typography>
                                                <Typography variant="caption" color="textSecondary">Abra um chamado virtual rápido</Typography>
                                            </Box>
                                            <Button 
                                                variant="outlined" 
                                                size="small"
                                                endIcon={<ArrowForward />}
                                                onClick={() => setCurrentTab(1)}
                                                sx={{ borderColor: config?.color, color: config?.color }}
                                            >
                                                Abrir
                                            </Button>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Paper>

                        {/* Visualização de Texto da Carta */}
                        <Paper elevation={2} sx={{ p: 4, borderRadius: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                                Conteúdo Informativo da Carta de Serviços
                            </Typography>
                            <Divider sx={{ mb: 3 }} />
                            
                            {loadingParams ? (
                                <Box display="flex" justifyContent="center" py={4}>
                                    <CircularProgress size={30} />
                                </Box>
                            ) : cartaParams.texto ? (
                                <Typography 
                                    variant="body1" 
                                    sx={{ 
                                        whiteSpace: 'pre-line', 
                                        textAlign: 'justify', 
                                        color: '#333',
                                        lineHeight: 1.7 
                                    }}
                                >
                                    {cartaParams.texto}
                                </Typography>
                            ) : (
                                <Box textAlign="center" py={4} bgcolor="#fafafa" borderRadius={2}>
                                    <HelpOutline color="disabled" sx={{ fontSize: 40, mb: 1 }} />
                                    <Typography variant="body2" color="textSecondary">
                                        Nenhum descritivo textual foi cadastrado. Por favor, utilize o botão de download acima para baixar a versão em anexo (PDF).
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>
                ) : (
                    /* ABA 1: FORMULÁRIO EXISTENTE DE SOLICITAÇÃO */
                    <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>Envie sua solicitação de carta de serviço</Typography>
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
                            Preencha os dados abaixo para gerar um ticket eletrônico interno. A secretaria responsável entrará em contato por e-mail.
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    label="Seu E-mail Institucional ou Pessoal" 
                                    name="email" 
                                    value={formData.email} 
                                    onChange={handleChange} 
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    select
                                    label="Selecione o Tipo de Serviço Desejado"
                                    name="letterType"
                                    value={formData.letterType}
                                    onChange={handleChange}
                                >
                                    {letterTypes.map((type) => (
                                        <MenuItem key={type} value={type}>
                                            {type}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Descrição Completa do Pedido (mínimo 10 caracteres)"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    multiline
                                    rows={4}
                                    placeholder="Escreva detalhes como CPF/CNPJ, endereço do local do fato ou especificidades necessárias para a emissão/atendimento do serviço."
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Tooltip
                                    title={submissionLimitReached ? "Você atingiu o limite diário de 3 solicitações." : ""}
                                    placement="top"
                                    arrow
                                >
                                    <span>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            sx={{ backgroundColor: config?.color || '#1976d2', px: 4, fontWeight: 'bold' }}
                                            onClick={handleSubmit}
                                            disabled={loading || submissionLimitReached}
                                        >
                                            {loading ? <CircularProgress size={24} color="inherit" /> : <Send sx={{ mr: 1, fontSize: 18 }} />}
                                            Protocolar Pedido de Serviço
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>

                        {ticketNumber && (
                            <Paper elevation={1} sx={{ p: 3, mt: 3, borderLeft: '6px solid #2e7d32', backgroundColor: '#f4fbf7' }}>
                                <Typography variant="body1" color="success.main" fontWeight="bold">
                                    Sua solicitação de serviço foi enviada com sucesso!
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Número do Ticket Único: <strong style={{ color: '#1976d2', fontSize: '1.1rem' }}>{ticketNumber}</strong>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">Status de Entrada: Criado</Typography>
                                <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                                    Importante: Guarde este número para consultar o deferimento da sua demanda.
                                </Typography>
                            </Paper>
                        )}
                    </Paper>
                )}
            </Box>

            <Box sx={{ flex: { xs: 1, md: 1.5 }, minWidth: 250 }}>
                <SidebarNavigation noticia={true} />
            </Box>

            <Snackbar open={success} autoHideDuration={4000} onClose={() => setSuccess(false)}>
                <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
                    Carta de serviço enviada com sucesso! Código do Ticket: {ticketNumber}.
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default ServiceLetterPage;